/**
 * Grow Overview — the admin (`ActsCareAdmin`) reporting page at `/Grow/Overview`.
 *
 * Org-wide course reporting: what is running, who is running it, and what is slipping.
 * Deliberately *not* personal; the per-user view lives on `/Dashboard`. The care-side equivalent
 * is `/Counselling/Overview`.
 *
 * Data rules:
 *  - Every read goes through an SDK service hook. No `useHttpClient()` / `getApiUrl()`.
 *  - The shared hooks in `src/pages/Overview/Hooks/` take a domain, so this page fetches course data
 *    only. `dashboardStats()` polls every 60s; charts, workload and lists refresh on demand.
 *  - Server-side aggregates that do not exist in the SDK yet are specced in `_deploy/_newEndPoints/`.
 *    The hooks detect them by capability and fall back to grouping the streamed lists where that is
 *    possible; where it is not, the section says so rather than showing a misleading empty chart.
 */

import { useMemo } from 'react';
import {
  ActionIcon, Group, SimpleGrid, Stack, Text, Title, Tooltip, UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle, IconBook, IconCalendarEvent, IconCalendarWeek, IconCertificate,
  IconChecklist, IconDoorEnter, IconListDetails, IconRefresh,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useRegistrationService, useRegistrationStatusService } from '@actschurch/ccms-sdk';
import { Stat, StatusKPICard, StatusKPISkeleton, getStatusIcon } from '@/components';
import BreakdownBars from '@/pages/Overview/Components/BreakdownBars';
import CriticalListCard from '@/pages/Overview/Components/CriticalListCard';
import OverviewSection from '@/pages/Overview/Components/OverviewSection';
import WorkloadTable from '@/pages/Overview/Components/WorkloadTable';
import useLookupStatuses from '@/pages/Overview/Hooks/useLookupStatuses';
import useOverviewCharts from '@/pages/Overview/Hooks/useOverviewCharts';
import useOverviewLists from '@/pages/Overview/Hooks/useOverviewLists';
import useOverviewStats from '@/pages/Overview/Hooks/useOverviewStats';
import { useStatusCounts } from '@/pages/Overview/Hooks/useStatusCounts';

/** A KPI tile that navigates when it has somewhere to go. */
function LinkedStat({
  label, value, icon, to, description,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  to?: string;
  description?: string;
}) {
  const navigate = useNavigate();
  const tile = <Stat label={label} value={value} icon={icon} description={description} />;

  if (!to) return tile;
  return (
    <UnstyledButton onClick={() => navigate(to)} style={{ textAlign: 'left' }}>
      {tile}
    </UnstyledButton>
  );
}

export default function GrowOverviewPage() {
  const navigate = useNavigate();

  const registrationService = useRegistrationService();
  const registrationStatusService = useRegistrationStatusService();

  const { courseStats, loading: statsLoading, refresh: refreshStats } = useOverviewStats('course');
  const charts = useOverviewCharts('course');
  const lists = useOverviewLists('course');

  // Unlike `RegistrationsListView`, which shows an include-list of three statuses, the Overview
  // shows the whole distribution — it is the one page whose job is the complete picture.
  const registrationStatuses = useLookupStatuses(registrationStatusService);

  const registrationCounts = useStatusCounts(
    registrationStatuses,
    (statusID) =>
      registrationService.listView({ take: 1, skip: 0, statusID: String(statusID) })
        .then((r) => r.totalCount ?? 0),
    () => registrationService.listView({ take: 1, skip: 0 }).then((r) => r.totalCount ?? 0),
  );

  const handleRefresh = () => {
    refreshStats();
    charts.refresh();
    lists.refresh();
    registrationCounts.refresh();
  };

  const refreshing = statsLoading || charts.loading || lists.loading;

  const statusKpiSkeletons = useMemo(
    () => Array.from({ length: 5 }).map((_, i) => <StatusKPISkeleton key={i} />),
    [],
  );

  return (
    <Stack gap="xl">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Group justify="space-between" align="flex-end" wrap="nowrap">
        <div>
          <Title order={2}>Overview</Title>
          <Text c="dimmed" size="sm">
            Courses, registrations and the facilitators running them, across the whole organisation.
          </Text>
        </div>
        <Tooltip label="Refresh">
          <ActionIcon variant="light" size="lg" onClick={handleRefresh} loading={refreshing}>
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        <LinkedStat
          label="Pending Intakes"
          value={courseStats?.pendingIntakesCount ?? 0}
          icon={<IconDoorEnter size={20} color="var(--mantine-color-orange-6)" />}
          to="/Grow/Intakes"
        />
        <LinkedStat
          label="Active Courses"
          value={courseStats?.activeCoursesCount ?? 0}
          icon={<IconBook size={20} color="var(--mantine-color-blue-6)" />}
          to="/Grow/Courses"
        />
        <LinkedStat
          label="Open Intake Dates"
          value={courseStats?.openDatesCount ?? 0}
          icon={<IconCalendarEvent size={20} color="var(--mantine-color-teal-6)" />}
        />
        <LinkedStat
          label="Closing This Week"
          value={courseStats?.closingThisWeekCount ?? 0}
          icon={<IconAlertTriangle size={20} color="var(--mantine-color-red-6)" />}
        />
        <LinkedStat
          label="Active Registrations"
          value={courseStats?.activeRegistrationsCount ?? 0}
          icon={<IconChecklist size={20} color="var(--mantine-color-indigo-6)" />}
          to="/Grow/Registration"
        />
        <LinkedStat
          label="Sessions Today"
          value={courseStats?.sessionsTodayCount ?? 0}
          icon={<IconCalendarEvent size={20} color="var(--mantine-color-green-6)" />}
        />
        <LinkedStat
          label="Sessions This Week"
          value={courseStats?.sessionsThisWeekCount ?? 0}
          icon={<IconCalendarWeek size={20} color="var(--mantine-color-teal-6)" />}
        />
        <LinkedStat
          label="Completed This Month"
          value={courseStats?.completedThisMonthCount ?? 0}
          icon={<IconCertificate size={20} color="var(--mantine-color-green-6)" />}
        />
      </SimpleGrid>

      <OverviewSection
        title="Registrations by status"
        subtitle="Every status in the lookup, counted across all registrations"
        viewAllTo="/Grow/Registration"
      >
        <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="md">
          {registrationCounts.loading && registrationCounts.rows.length === 0 ? statusKpiSkeletons : (
            <>
              <StatusKPICard
                icon={IconListDetails}
                label="All"
                count={registrationCounts.allCount}
                color="var(--mantine-primary-color-filled)"
                active={false}
                onClick={() => navigate('/Grow/Registration')}
              />
              {registrationCounts.rows.map((status) => (
                <StatusKPICard
                  key={status.statusID}
                  icon={getStatusIcon(status.icon ?? undefined)}
                  label={status.name ?? '—'}
                  count={status.count}
                  color={status.color || 'var(--mantine-primary-color-filled)'}
                  active={false}
                  onClick={() => navigate('/Grow/Registration')}
                />
              ))}
            </>
          )}
        </SimpleGrid>
      </OverviewSection>

      <OverviewSection title="Course reach" subtitle="Registrations grouped by course and location">
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <BreakdownBars
            title="Registrations by course"
            subtitle="Which courses people are actually enrolling on"
            data={charts.registrationsByCourse.data}
            unavailableMessage={charts.registrationsByCourse.unavailable}
            loading={charts.loading}
            entityLabel="courses"
            valueLabel="Registrations"
            totalLabel="All time"
          />
          <BreakdownBars
            title="Registrations by location"
            subtitle="Where courses are being run"
            data={charts.registrationsByLocation.data}
            unavailableMessage={charts.registrationsByLocation.unavailable}
            loading={charts.loading}
            entityLabel="locations"
            valueLabel="Registrations"
            totalLabel="All time"
          />
        </SimpleGrid>
      </OverviewSection>

      <OverviewSection
        title="Facilitator load"
        subtitle="Who is running how many active registrations"
        viewAllTo="/Information/facilitators"
      >
        <WorkloadTable
          title="Facilitators"
          subtitle="Sorted by active registrations"
          rows={lists.facilitatorWorkload}
          loading={lists.loading}
          primaryLabel="Active registrations"
          secondaryLabel="Courses"
          rowRoutePrefix="/Information/facilitators/"
          viewAllTo="/Information/facilitators"
          emptyMessage="No facilitators have been set up yet."
        />
      </OverviewSection>

      <OverviewSection title="Needs attention" subtitle="Course work that is slipping">
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <CriticalListCard
            title="New registration intakes"
            description="Public sign-ups awaiting approval"
            icon={IconDoorEnter}
            color="var(--mantine-color-grape-6)"
            rows={lists.newIntakes}
            totalCount={lists.newIntakesTotal}
            loading={lists.loading}
            emptyMessage="No intakes in the queue."
            viewAllTo="/Grow/Intakes"
          />
          <CriticalListCard
            title="Behind schedule"
            description="Active registrations whose next session has already passed"
            icon={IconAlertTriangle}
            color="var(--mantine-color-red-6)"
            rows={lists.behindScheduleRegistrations}
            loading={lists.loading}
            viewAllTo="/Grow/Registration"
            emptyMessage="Every registration is on track."
          />
        </SimpleGrid>
      </OverviewSection>
    </Stack>
  );
}
